import React from 'react';
import { EnhancedImageUpload } from './EnhancedImageUpload';
import { ImageLibrary } from './ImageLibrary';
import { ImageEnhancementTool } from './ImageEnhancementTool';
import { EmbroideryConversionTool } from './EmbroideryConversionTool';
import { BackgroundRemovalTool } from './BackgroundRemovalTool';
import { FormatConversionTool } from './FormatConversionTool';
import { WatermarkTool } from './WatermarkTool';
import { ResizeCropTool } from './ResizeCropTool';
import { CompressionTool } from './CompressionTool';
import { FiltersTool } from './FiltersTool';
import { ProductPhotoTool } from './ProductPhotoTool';
import { SocialMediaTool } from './SocialMediaTool';
import { FaviconGeneratorTool } from './FaviconGeneratorTool';
import { ProjectsManager } from './ProjectsManager';
import { PresetBuilder } from './PresetBuilder';
import { HistoryPanel } from './HistoryPanel';
import { ExportOptions } from './ExportOptions';
import type { UploadedImage, ToolType } from '@/pages/ImageToolsHub';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ImageWorkspaceProps {
  images: UploadedImage[];
  setImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
  activeTool: ToolType;
  sessionId: string;
}

export const ImageWorkspace: React.FC<ImageWorkspaceProps> = ({
  images,
  setImages,
  activeTool,
  sessionId,
}) => {
  return (
    <main className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-16 border-b flex items-center px-6 bg-card">
        <h1 className="text-2xl font-bold">
          {activeTool === 'upload' && 'Upload Images'}
          {activeTool === 'enhance' && 'Enhance Images'}
          {activeTool === 'background-remove' && 'Remove Background'}
          {activeTool === 'format-convert' && 'Convert Format'}
          {activeTool === 'resize' && 'Resize & Crop'}
          {activeTool === 'compress' && 'Compress Images'}
          {activeTool === 'filters' && 'Filters & Effects'}
          {activeTool === 'watermark' && 'Add Watermark'}
          {activeTool === 'embroidery' && 'Embroidery Conversion'}
          {activeTool === 'product-photo' && 'Product Photography'}
          {activeTool === 'social-media' && 'Social Media Optimizer'}
          {activeTool === 'favicon-generator' && 'Favicon & Icon Generator'}
          {activeTool === 'projects' && 'Projects & Collections'}
          {activeTool === 'presets' && 'Workflow Presets'}
          {activeTool === 'history' && 'Processing History'}
          {activeTool === 'export' && 'Export Options'}
        </h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTool === 'upload' && (
          <EnhancedImageUpload images={images} setImages={setImages} />
        )}

        {activeTool !== 'upload' && images.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>No Images Uploaded</CardTitle>
              <CardDescription>
                Please upload images first using the Upload tool
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {activeTool !== 'upload' && images.length > 0 && (
          <div className="space-y-6">
            <ImageLibrary images={images} setImages={setImages} />
            
            {activeTool === 'enhance' && (
              <ImageEnhancementTool images={images} />
            )}
            
            {activeTool === 'embroidery' && (
              <EmbroideryConversionTool images={images} />
            )}
            
            {activeTool === 'background-remove' && (
              <BackgroundRemovalTool images={images} />
            )}
            
            {activeTool === 'format-convert' && (
              <FormatConversionTool images={images} />
            )}
            
            {activeTool === 'watermark' && (
              <WatermarkTool images={images} />
            )}
            
            {activeTool === 'resize' && (
              <ResizeCropTool images={images} />
            )}
            
            {activeTool === 'compress' && (
              <CompressionTool images={images} />
            )}
            
            {activeTool === 'filters' && (
              <FiltersTool images={images} />
            )}
            
            {activeTool === 'product-photo' && (
              <ProductPhotoTool images={images} />
            )}
            
            {activeTool === 'social-media' && (
              <SocialMediaTool images={images} />
            )}
            
            {activeTool === 'favicon-generator' && (
              <FaviconGeneratorTool images={images} />
            )}
            
            {activeTool === 'projects' && (
              <ProjectsManager />
            )}
            
            {activeTool === 'presets' && (
              <PresetBuilder />
            )}
            
            {activeTool === 'history' && (
              <HistoryPanel sessionId={sessionId} />
            )}
            
            {activeTool === 'export' && (
              <ExportOptions 
                images={images.map(img => ({
                  id: img.id,
                  name: img.name,
                  blob: img.file,
                  dataUrl: img.preview,
                }))}
              />
            )}
            
            {!['enhance', 'embroidery', 'background-remove', 'format-convert', 'watermark', 'resize', 'compress', 'filters', 'product-photo', 'social-media', 'favicon-generator', 'projects', 'presets', 'history', 'export'].includes(activeTool) && (
              <Card>
                <CardHeader>
                  <CardTitle>Tool Options</CardTitle>
                  <CardDescription>
                    Configure settings for {activeTool}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Tool interface coming soon for {activeTool}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </main>
  );
};
